/**
 * Competitive comparison. Columns are categories, not single products, to stay
 * accurate and defensible — "partial" is used wherever a named product in the
 * category genuinely does some version of the row.
 */
const Comparison = () => {
  const columns = [
    { key: 'us', label: 'Solidly Meet', sub: 'this app', highlight: true },
    { key: 'notetaker', label: 'Meeting notetakers', sub: 'Otter, Granola, Fireflies' },
    { key: 'claude', label: 'Raw Claude', sub: 'claude.ai in a tab' },
  ];

  // 'yes' | 'no' | 'partial'
  const rows = [
    { feature: 'Preps you before the meeting — agenda, calendar', us: 'yes', notetaker: 'no', claude: 'no' },
    { feature: 'Helps you during the meeting, live', us: 'yes', notetaker: 'partial', claude: 'no' },
    { feature: 'No bot joins the call', us: 'yes', notetaker: 'partial', claude: 'yes' },
    { feature: 'Hears the conversation at all', us: 'yes', notetaker: 'yes', claude: 'no' },
    { feature: 'Meeting data stays on your machine', us: 'yes', notetaker: 'no', claude: 'partial' },
    { feature: 'Voice recognition speaker ID, no bot required', us: 'yes', notetaker: 'partial', claude: 'no' },
    { feature: 'Notes delivered to your tools via MCP', us: 'yes', notetaker: 'partial', claude: 'partial' },
    { feature: 'Next steps land in your Claude history', us: 'yes', notetaker: 'no', claude: 'partial' },
    { feature: 'One-time price — no new subscription', us: 'yes', notetaker: 'no', claude: 'yes' },
  ];

  const Cell = ({ state }) => {
    if (state === 'yes') return <span className="cmp-mark cmp-yes" title="Yes">●</span>;
    if (state === 'partial') return <span className="cmp-mark cmp-partial" title="Partial">◐</span>;
    return <span className="cmp-mark cmp-no" title="No">○</span>;
  };

  return (
    <section className="compare" id="compare">
      <div className="section-container">
        <p className="overline overline--center">HOW IT COMPARES</p>
        <h2 className="section-title">Notetakers recap. Claude can’t hear. This does both jobs.</h2>
        <p className="section-subtitle">
          Meeting notetakers summarize what already happened — from their cloud, often via a bot in
          your call. Claude is brilliant but deaf to your meetings. Solidly Meet puts the two
          together, on your machine.
        </p>

        <div className="cmp-table-wrap">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-feature-head" scope="col">
                  Capability
                </th>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={c.highlight ? 'cmp-col-us' : ''}>
                    <span className="cmp-col-label">{c.label}</span>
                    <span className="cmp-col-sub mono">{c.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <th scope="row" className="cmp-feature-cell">
                    {r.feature}
                  </th>
                  {columns.map((c) => (
                    <td key={c.key} className={c.highlight ? 'cmp-col-us' : ''}>
                      <Cell state={r[c.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cmp-legend mono">
          <span><span className="cmp-mark cmp-yes">●</span> yes</span>
          <span><span className="cmp-mark cmp-partial">◐</span> partial</span>
          <span><span className="cmp-mark cmp-no">○</span> no</span>
        </div>

        <p className="cmp-footnote">
          Generalized across the named products from publicly documented capabilities as of 2026 —
          Granola, for instance, is also bot-free, and some notetakers offer live chat or
          integrations, hence the half-marks. The honest difference: they end at a recap in their
          cloud; this works with you live and hands off to your Claude.
        </p>
      </div>
    </section>
  );
};

export default Comparison;
